import React from 'react';

import CheckInventoryButton from '../check-inventory-button/check-inventory-button';
import MyStoreMarker from '../my-store-marker/my-store-marker';
import ProductAvailabilityMessage from '../product-availability-message/product-availability-message';

import { useProductEntryDataLayerValues } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getPrioritizedArticleStatusProduct } from '../../shared/utils/product/product';

import { BundleData, ProductData } from '../../shared/types/graphql-types';

import './product-availability.scss';

interface ProductAvailabilityProps {
  className?: string;
  hideNearbyStoresLink?: boolean;
  onCheckInventoryClick?: () => void;
  prebuiltPackage?: BundleData;
  product: ProductData;
  quantity?: number;
  rearProduct?: ProductData | null;
  rearQuantity?: number;
  renderAddToCartBtn?: () => void;
  renderCheckIcon?: React.ReactElement;
  showMyStoreMarker?: boolean;
  transformAvailabilityMessageText?: (message: string) => void;
}

function ProductAvailability(props: ProductAvailabilityProps) {
  const {
    className,
    hideNearbyStoresLink,
    product,
    rearProduct,
    quantity,
    rearQuantity,
    prebuiltPackage,
    onCheckInventoryClick,
    showMyStoreMarker = true,
    renderCheckIcon,
    transformAvailabilityMessageText,
    renderAddToCartBtn
  } = props;

  const isStaggered = Boolean(rearProduct);
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const articleStatusProduct = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(
        prebuiltPackage?.product1,
        prebuiltPackage?.product2
      )
    : getPrioritizedArticleStatusProduct(product, rearProduct);

  let showCheckNearbyStoresLink;

  const { availabilityMessage, availabilityMessageRank, stockLevelStatus } =
    articleStatusProduct?.stock || {};

  const nearbyStoresStockCount =
    articleStatusProduct?.stock?.nearbyStoresStockCount;

  if (!isStaggered && nearbyStoresStockCount < 4) {
    showCheckNearbyStoresLink = false;
  } else if (isStaggered && nearbyStoresStockCount < 2) {
    showCheckNearbyStoresLink = false;
  } else {
    showCheckNearbyStoresLink =
      !hideNearbyStoresLink &&
      articleStatusProduct?.stock?.showCheckNearbyStoresLink;
  }

  return (
    <div
      className={`${className || ''} product-availability`}
      styleName="container"
    >
      <div className="product-availability__content" styleName="content">
        {availabilityMessage && (
          <span className="product-availability__availability">
            <ProductAvailabilityMessage
              availabilityMessage={
                transformAvailabilityMessageText
                  ? transformAvailabilityMessageText(availabilityMessage)
                  : availabilityMessage
              }
              availabilityMessageRank={availabilityMessageRank}
              renderCheckIcon={renderCheckIcon}
              stockLevelCode={stockLevelStatus?.code}
            />
            {showMyStoreMarker && <MyStoreMarker showAtPrefix />}
          </span>
        )}
        {showCheckNearbyStoresLink && (
          <CheckInventoryButton
            className="product-availability__check-inv-btn"
            hideCartItems
            initialQuantity={quantity}
            initialRearQuantity={rearQuantity}
            onCheckInventoryClick={onCheckInventoryClick}
            onSetStoreClick={storeDataLayerValues =>
              trackAction(
                'product_change_store',
                getProductEntryDataLayerValues(product),
                storeDataLayerValues
              )
            }
            productItem={{
              prebuiltPackage,
              product,
              rearProduct
            }}
            renderAddToCartBtn={renderAddToCartBtn}
            styleName="check-inv-btn"
          />
        )}
      </div>
    </div>
  );
}

export default ProductAvailability;
